import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Search } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'

function KlassSelect({ klasses, value, onChange }) {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const filteredKlasses = useMemo(() => {
    if (!searchTerm) return klasses
    const term = searchTerm.toLowerCase()
    return klasses.filter((klass) => klass.displayName.toLowerCase().includes(term))
  }, [klasses, searchTerm])

  const selectedKlass = klasses.find((k) => k.id === value)

  function handleSelect(klassId) {
    onChange(klassId)
    setOpen(false)
    setSearchTerm('')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between mt-1">
          {selectedKlass ? selectedKlass.displayName : 'Select a class...'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-2" align="start">
        <div className="flex items-center border-b pb-2 mb-2">
          <Search className="h-4 w-4 text-gray-400 mr-2" />
          <Input
            placeholder="Search classes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-0 p-0 h-8 focus-visible:ring-0"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredKlasses.length === 0 ? (
            <p className="text-sm text-gray-500 py-2 text-center">No classes found</p>
          ) : (
            filteredKlasses.map((klass) => (
              <div
                key={klass.id}
                className={`flex items-center px-2 py-2 cursor-pointer rounded hover:bg-gray-100 ${
                  value === klass.id ? 'bg-purple-50' : ''
                }`}
                onClick={() => handleSelect(klass.id)}
              >
                <div className="flex-1">
                  <div className="font-medium text-sm">{klass.displayName}</div>
                  <div className="text-xs text-gray-500">{klass.dateRange}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

KlassSelect.propTypes = {
  klasses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      displayName: PropTypes.string.isRequired,
      dateRange: PropTypes.string
    })
  ).isRequired,
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired
}

export default KlassSelect
